import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { BsTools, BsArrowRepeat, BsPlusCircle, BsXCircle, BsHddNetwork, BsPencil, BsTrash, BsChatDots, BsTerminal, BsDownload, BsPauseCircle, BsPlayCircle } from 'react-icons/bs';
import ProjectSetupModal from '../components/ajustes/ProjectSetupModal';
import AgentInstallModal from '../components/ajustes/AgentInstallModal';
import { apiFetch, parseJsonSafe } from '../utils/api';
import {
  FILTERS, PRIORITY, StatusPill, SourceIcon, devApi, formatDateTime
} from '../components/ajustes/ajustesShared';

const Modal = ({ title, onClose, children, footer, width = 560 }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1050, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 12px', overflowY: 'auto' }}>
    <div className="card" style={{ width: '100%', maxWidth: width }}>
      <div className="card-header d-flex justify-content-between align-items-center">
        <strong>{title}</strong>
        <button type="button" className="btn btn-sm btn-link text-dark p-0" onClick={onClose} aria-label="Cerrar"><BsXCircle size={20} /></button>
      </div>
      <div className="card-body">{children}</div>
      {footer && <div className="card-footer d-flex justify-content-end gap-2">{footer}</div>}
    </div>
  </div>
);

const authGet = async (path) => {
  const res = await apiFetch(path, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  const data = await parseJsonSafe(res);
  if (!res.ok) throw new Error(data.message || 'Error cargando datos');
  return data;
};

const EMPTY_CONN = { project_id: null, server_id: null, staging_url: '', production_url: '', notify_client_staging: true, notify_client_published: true, active: true, claude_model: 'opus', claude_effort: 'high', claude_permission_mode: 'autonomo' };
// Cómo trabaja Claude en el servidor (se manda con cada trabajo). Solo los modelos grandes.
const CLAUDE_MODELS = { opus: 'Claude Opus 5.5', fable: 'Claude Fable 5.1' };
const CLAUDE_EFFORTS = { medium: 'Medio', high: 'Alto', xhigh: 'Muy alto', max: 'Máximo' };
const CLAUDE_MODES = {
  autonomo: 'Autónomo: no pregunta ni bloquea nada (salvo los límites fijos del servidor)',
  auto: 'Auto: un clasificador revisa cada acción y deniega las dudosas'
};

const AjustesPage = () => {
  const navigate = useNavigate();
  const isAdmin = localStorage.getItem('role') === 'admin';
  const [tab, setTab] = useState('ajustes');
  const [filter, setFilter] = useState('atencion');
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ byStatus: {}, attention: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [autoSend, setAutoSend] = useState(null);

  // Proyectos conectados
  const [connections, setConnections] = useState([]);
  const [servers, setServers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [connModal, setConnModal] = useState(null);
  const [connError, setConnError] = useState('');
  const [setupConn, setSetupConn] = useState(null);
  const [savingConn, setSavingConn] = useState(false);

  // Servidores de ajustes (los VPS con el agente)
  const [installServer, setInstallServer] = useState(null);
  const [serverModal, setServerModal] = useState(null);   // { id?, name }
  const [serverError, setServerError] = useState('');
  const [savingServer, setSavingServer] = useState(false);

  // Nuevo ajuste a mano
  const [newModal, setNewModal] = useState(false);
  const [form, setForm] = useState({ client_id: null, project_id: null, title: '', description: '', priority: 'media' });
  const [formError, setFormError] = useState('');
  const [savingNew, setSavingNew] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const f = FILTERS.find((x) => x.key === filter) || FILTERS[0];
      const qs = new URLSearchParams({ ...f.params });
      if (q.trim()) qs.append('q', q.trim());
      const [list, c] = await Promise.all([devApi(`?${qs.toString()}`), devApi('/counts')]);
      setItems(list.items || []);
      setCounts(c);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter, q]);

  const loadConnections = useCallback(async () => {
    try {
      const [conn, srv] = await Promise.all([devApi('/connections'), devApi('/servers')]);
      setConnections(conn.items || []);
      setServers(srv.items || []);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => { if (tab === 'ajustes') loadList(); }, [tab, loadList]);
  useEffect(() => { if (tab === 'conexiones' || tab === 'servidores') loadConnections(); }, [tab, loadConnections]);
  useEffect(() => {
    devApi('/settings').then((d) => setAutoSend(Boolean(d.clientMessagesAuto))).catch(() => {});
  }, []);

  const ensureProjects = async () => {
    if (projects.length) return;
    try {
      const data = await authGet('/api/projects?page=1&pageSize=500&sort=titulo');
      setProjects(data.items || []);
    } catch (err) { setError(err.message); }
  };

  const toggleAutoSend = async (enabled) => {
    if (enabled && !window.confirm('Las dudas de Claude y los avisos saldrán al cliente sin que los revises, y sus respuestas volverán solas a Claude. ¿Activarlo?')) return;
    setAutoSend(enabled);
    try {
      const d = await devApi('/settings', { method: 'PUT', body: { clientMessagesAuto: enabled } });
      setAutoSend(Boolean(d.clientMessagesAuto));
    } catch (err) {
      setAutoSend(!enabled);
      setError(err.message);
    }
  };

  // ---- conexiones
  const openConn = async (row) => {
    setConnError('');
    await ensureProjects();
    setConnModal(row ? {
      project_id: row.project_id,
      server_id: row.server_id,
      staging_url: row.staging_url || '',
      production_url: row.production_url || '',
      notify_client_staging: row.notify_client_staging !== false,
      notify_client_published: row.notify_client_published !== false,
      active: row.active !== false,
      claude_model: row.claude_model || 'opus',
      claude_effort: row.claude_effort || 'high',
      claude_permission_mode: row.claude_permission_mode || 'autonomo',
      editing: true
    } : { ...EMPTY_CONN });
  };

  const saveConn = async () => {
    if (!connModal.project_id) { setConnError('Elige el proyecto'); return; }
    if (!connModal.server_id) { setConnError('Elige el servidor'); return; }
    setSavingConn(true);
    setConnError('');
    try {
      await devApi(`/connections/${connModal.project_id}`, { method: 'PUT', body: connModal });
      setConnModal(null);
      loadConnections();
    } catch (err) {
      setConnError(err.message);
    } finally {
      setSavingConn(false);
    }
  };

  const deleteConn = async (row) => {
    if (!window.confirm(`¿Desconectar "${row.project_title}"? Sus próximos ajustes quedarán para hacerlos a mano.`)) return;
    try {
      await devApi(`/connections/${row.project_id}`, { method: 'DELETE' });
      loadConnections();
    } catch (err) { setError(err.message); }
  };

  // ---- servidores
  const saveServer = async () => {
    if (!serverModal.name.trim()) { setServerError('Escribe el nombre del servidor'); return; }
    setSavingServer(true);
    setServerError('');
    try {
      if (serverModal.id) await devApi(`/servers/${serverModal.id}`, { method: 'PUT', body: { name: serverModal.name } });
      else {
        const created = await devApi('/servers', { method: 'POST', body: { name: serverModal.name } });
        setServerModal(null);
        await loadConnections();
        setInstallServer(created);   // recién creado: lo siguiente es instalar el agente
        return;
      }
      setServerModal(null);
      loadConnections();
    } catch (err) {
      setServerError(err.message);
    } finally {
      setSavingServer(false);
    }
  };

  const toggleServer = async (s) => {
    if (s.active && !window.confirm(`¿Pausar «${s.name}»? Sus proyectos no recibirán ajustes y lo que tenga en cola volverá a pendiente.`)) return;
    try {
      await devApi(`/servers/${s.id}`, { method: 'PUT', body: { active: !s.active } });
      loadConnections();
    } catch (err) { setError(err.message); }
  };

  const deleteServer = async (s) => {
    const n = Number(s.projects) || 0;
    if (!window.confirm(`¿Eliminar el servidor «${s.name}»?${n ? ` Sus ${n} proyecto(s) quedarán sin servidor.` : ''} El agente instalado dejará de poder conectarse.`)) return;
    try {
      await devApi(`/servers/${s.id}`, { method: 'DELETE' });
      loadConnections();
    } catch (err) { setError(err.message); }
  };

  // ---- nuevo ajuste
  const openNew = async () => {
    setForm({ client_id: null, project_id: null, title: '', description: '', priority: 'media' });
    setFormError('');
    setNewModal(true);
    await ensureProjects();
  };

  const saveNew = async () => {
    if (!form.client_id) { setFormError('Elige el cliente'); return; }
    if (!form.title.trim()) { setFormError('Escribe qué hay que cambiar'); return; }
    setSavingNew(true);
    setFormError('');
    try {
      const created = await devApi('', { method: 'POST', body: { ...form, source: 'manual' } });
      setNewModal(false);
      navigate(`/cambios/${created.id}`);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSavingNew(false);
    }
  };

  // Los ajustes son de proyectos: se ofrecen los clientes que tienen alguno.
  const clientOptions = useMemo(() => {
    const seen = new Map();
    projects.forEach((p) => {
      if (p.client_id && !seen.has(p.client_id)) seen.set(p.client_id, p.client_name || `Cliente #${p.client_id}`);
    });
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [projects]);
  const projectOptions = useMemo(() => projects.map((p) => ({
    value: p.id,
    clientId: p.client_id,
    label: `#${p.id} · ${p.titulo}${p.client_name ? ` (${p.client_name})` : ''}`
  })), [projects]);
  const serverOptions = useMemo(() => servers.map((s) => ({ value: s.id, label: s.name })), [servers]);
  const newProjectOptions = projectOptions.filter((p) => !form.client_id || p.clientId === form.client_id);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <h2 className="mb-0 d-flex align-items-center"><BsTools className="me-2" /> Cambios solicitados por clientes</h2>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          {autoSend !== null && (
            <div className="form-check form-switch mb-0 me-2" title="Desactivado: las dudas de Claude y los avisos quedan en borrador hasta que los revisas, y las respuestas del cliente esperan a que las leas. Activado: los mensajes salen solos por email o WhatsApp y las respuestas vuelven solas a Claude.">
              <input className="form-check-input" type="checkbox" id="devAutoSend" checked={autoSend} disabled={!isAdmin} onChange={(e) => toggleAutoSend(e.target.checked)} />
              <label className="form-check-label small" htmlFor="devAutoSend">Hablar con el cliente sin revisión</label>
            </div>
          )}
          <button type="button" className="btn btn-outline-secondary" onClick={() => (tab === 'ajustes' ? loadList() : loadConnections())} title="Actualizar"><BsArrowRepeat /></button>
          {tab === 'ajustes' && (
            <button type="button" className="btn btn-primary d-flex align-items-center" onClick={openNew}><BsPlusCircle className="me-2" /> Nuevo cambio</button>
          )}
          {tab === 'conexiones' && isAdmin && (
            <button type="button" className="btn btn-primary d-flex align-items-center" onClick={() => openConn(null)}><BsHddNetwork className="me-2" /> Conectar proyecto</button>
          )}
          {tab === 'servidores' && isAdmin && (
            <button type="button" className="btn btn-primary d-flex align-items-center" onClick={() => { setServerError(''); setServerModal({ name: '' }); }}><BsPlusCircle className="me-2" /> Añadir servidor</button>
          )}
        </div>
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button type="button" className={`nav-link ${tab === 'ajustes' ? 'active' : ''}`} onClick={() => setTab('ajustes')}>
            Cambios {counts.attention > 0 && <span className="badge bg-danger ms-1">{counts.attention}</span>}
          </button>
        </li>
        <li className="nav-item">
          <button type="button" className={`nav-link ${tab === 'servidores' ? 'active' : ''}`} onClick={() => setTab('servidores')}>Servidores</button>
        </li>
        <li className="nav-item">
          <button type="button" className={`nav-link ${tab === 'conexiones' ? 'active' : ''}`} onClick={() => setTab('conexiones')}>Proyectos conectados</button>
        </li>
      </ul>

      {error && <div className="alert alert-danger">{error}</div>}

      {tab === 'ajustes' && (
        <>
          <div className="card mb-3">
            <div className="card-body d-flex flex-wrap gap-2 align-items-center">
              <div className="btn-group btn-group-sm" role="group">
                {FILTERS.map((f) => (
                  <button key={f.key} type="button" className={`btn ${filter === f.key ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFilter(f.key)}>{f.label}</button>
                ))}
              </div>
              <input className="form-control form-control-sm flex-grow-1" style={{ minWidth: 200, maxWidth: 360 }} placeholder="Buscar por ajuste, cliente o proyecto…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
          </div>
          <div className="card">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Cliente</th>
                    <th>Proyecto</th>
                    <th>Ajuste</th>
                    <th>Estado</th>
                    <th>Actualizado</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="6" className="text-center text-muted py-4">Cargando…</td></tr>
                  ) : !items.length ? (
                    <tr><td colSpan="6" className="text-center text-muted py-4">{filter === 'atencion' ? 'No hay cambios esperándote.' : 'No hay cambios.'}</td></tr>
                  ) : items.map((r) => (
                    <tr key={r.id} style={{ cursor: 'pointer', fontWeight: r.staff_attention ? 600 : 400 }} onClick={() => navigate(`/cambios/${r.id}`)}>
                      <td className="text-nowrap">
                        {r.staff_attention && <span className="badge bg-danger rounded-circle me-1 p-1" style={{ verticalAlign: 'middle' }}> </span>}
                        {r.id}
                      </td>
                      <td>{r.client_name || '-'}</td>
                      <td className="small">
                        {r.project_title || <span className="text-danger">Sin proyecto</span>}
                        {r.project_title && !r.connected && <div className="text-muted">Sin servidor</div>}
                      </td>
                      <td>
                        <span className="me-1"><SourceIcon source={r.source} /></span>
                        {r.title}
                        {r.priority === 'alta' && <span className="badge bg-danger ms-1">{PRIORITY.alta}</span>}
                        {r.pending_client_messages > 0 && (
                          <span className="badge bg-warning text-dark ms-1" title="Mensajes al cliente pendientes de revisar"><BsChatDots /> {r.pending_client_messages}</span>
                        )}
                      </td>
                      <td><StatusPill status={r.status} /></td>
                      <td className="small text-muted text-nowrap">{formatDateTime(r.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === 'servidores' && (
        <>
          <div className="alert alert-light border small">
            Los servidores son los VPS donde está instalado el <strong>agente de ajustes</strong>: cada pocos minutos piden a Automationia los ajustes aprobados de sus proyectos y los aplican con Claude Code. Son independientes de los servidores de Monitorización. Pasos: 1) <strong>Añadir servidor</strong>; 2) <strong>Instalar agente</strong>, que da los comandos con los tokens ya puestos; 3) conectar sus proyectos en <em>Proyectos conectados</em>.
          </div>
          <div className="card">
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead>
                  <tr>
                    <th>Servidor</th>
                    <th>Estado</th>
                    <th>Agente</th>
                    <th>Publicación</th>
                    <th>Proyectos</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {!servers.length ? (
                    <tr><td colSpan="6" className="text-center text-muted py-4">Ningún servidor todavía. Añade el VPS del primer proyecto.</td></tr>
                  ) : servers.map((s) => (
                    <tr key={s.id} className={s.active ? '' : 'text-muted'}>
                      <td>
                        {s.name}
                        {s.last_hostname && <div className="small text-muted">{s.last_hostname}</div>}
                      </td>
                      <td className="small">
                        {!s.active ? <span className="badge bg-secondary">Pausado</span>
                          : !s.last_seen_at ? <span className="badge bg-warning text-dark">Agente sin instalar</span>
                            : (Date.now() - new Date(s.last_seen_at).getTime() > 30 * 60000)
                              ? <span className="badge bg-danger">Sin contacto</span>
                              : <span className="badge bg-success">Conectado</span>}
                        {s.live_jobs > 0 && <div className="text-muted">{s.live_jobs} trabajo(s) en marcha</div>}
                      </td>
                      <td className="small text-muted">{s.last_seen_at ? `Visto ${formatDateTime(s.last_seen_at)}` : '-'}</td>
                      <td className="small text-muted">{s.last_publish_seen_at ? `Visto ${formatDateTime(s.last_publish_seen_at)}` : '-'}</td>
                      <td>{s.projects || 0}</td>
                      <td className="text-end text-nowrap">
                        {isAdmin && (
                          <>
                            <button type="button" className="btn btn-sm btn-outline-primary me-1" onClick={() => setInstallServer(s)} title="Instalar el agente en este servidor (comandos y tokens)"><BsDownload /> Instalar agente</button>
                            <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => { setServerError(''); setServerModal({ id: s.id, name: s.name }); }} title="Cambiar nombre"><BsPencil /></button>
                            <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => toggleServer(s)} title={s.active ? 'Pausar' : 'Reanudar'}>{s.active ? <BsPauseCircle /> : <BsPlayCircle />}</button>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deleteServer(s)} title="Eliminar"><BsTrash /></button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === 'conexiones' && (
        <>
          <div className="alert alert-light border small">
            Un proyecto conectado manda sus ajustes al agente de su servidor, que los aplica con Claude Code. Las carpetas, la base de datos y si se trabaja en staging o directamente en producción se configuran <strong>en el propio servidor</strong>, en <code>/opt/ajustes/proyectos/&lt;id del proyecto&gt;.env</code>. Pasos: 1) añadir el servidor e instalar el agente en la pestaña <em>Servidores</em>; 2) <strong>Conectar proyecto</strong> aquí; 3) <strong>Configurar en el servidor</strong>, que te guía paso a paso para preparar ese proyecto.
          </div>
          <div className="card">
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead>
                  <tr>
                    <th>Id</th>
                    <th>Proyecto</th>
                    <th>Servidor</th>
                    <th>Staging</th>
                    <th>Producción</th>
                    <th>Claude</th>
                    <th>Avisos al cliente</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {!connections.length ? (
                    <tr><td colSpan="8" className="text-center text-muted py-4">Ningún proyecto conectado todavía.</td></tr>
                  ) : connections.map((c) => (
                    <tr key={c.project_id} className={c.active ? '' : 'text-muted'}>
                      <td><code>{c.project_id}</code></td>
                      <td>
                        {c.project_title}
                        <div className="small text-muted">{c.client_name || ''}{!c.active && ' · pausado'}</div>
                      </td>
                      <td className="small">
                        {c.server_name || <span className="text-danger">Sin servidor</span>}
                        {c.server_name && c.server_active === false && <div className="text-warning">Servidor pausado</div>}
                        {c.server_last_seen_at && <div className="text-muted">Visto {formatDateTime(c.server_last_seen_at)}</div>}
                      </td>
                      <td className="small">{c.staging_url ? <a href={c.staging_url} target="_blank" rel="noreferrer">{c.staging_url}</a> : <span className="text-muted">Sin staging</span>}</td>
                      <td className="small">{c.production_url ? <a href={c.production_url} target="_blank" rel="noreferrer">{c.production_url}</a> : '-'}</td>
                      <td className="small">
                        {CLAUDE_MODELS[c.claude_model] || CLAUDE_MODELS.opus}
                        <div className="text-muted">esfuerzo {(CLAUDE_EFFORTS[c.claude_effort] || 'Alto').toLowerCase()} · {c.claude_permission_mode === 'auto' ? 'modo auto' : 'autónomo'}</div>
                      </td>
                      <td className="small">
                        {c.notify_client_staging && c.staging_url ? 'Listo para probar' : ''}
                        {c.notify_client_staging && c.staging_url && c.notify_client_published ? ' · ' : ''}
                        {c.notify_client_published ? 'Publicado' : ''}
                        {!c.notify_client_published && !(c.notify_client_staging && c.staging_url) && <span className="text-muted">Ninguno</span>}
                      </td>
                      <td className="text-end text-nowrap">
                        <button type="button" className="btn btn-sm btn-outline-primary me-1" onClick={() => setSetupConn(c)} title="Guía paso a paso para preparar este proyecto en su servidor"><BsTerminal /> Configurar en el servidor</button>
                        {isAdmin && (
                          <>
                            <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => openConn(c)} title="Editar"><BsPencil /></button>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deleteConn(c)} title="Desconectar"><BsTrash /></button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {setupConn && <ProjectSetupModal connection={setupConn} onClose={() => setSetupConn(null)} />}
      {installServer && <AgentInstallModal server={installServer} onClose={() => { setInstallServer(null); loadConnections(); }} />}

      {serverModal && (
        <Modal
          title={serverModal.id ? 'Cambiar el nombre del servidor' : 'Añadir un servidor de ajustes'}
          onClose={() => setServerModal(null)}
          width={460}
          footer={(
            <>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setServerModal(null)}>Cancelar</button>
              <button type="button" className="btn btn-primary" disabled={savingServer} onClick={saveServer}>{savingServer ? 'Guardando…' : (serverModal.id ? 'Guardar' : 'Añadir e instalar')}</button>
            </>
          )}
        >
          {serverError && <div className="alert alert-danger py-2">{serverError}</div>}
          <label className="form-label">Nombre</label>
          <input className="form-control" autoFocus placeholder="VPS cliente1 (Hetzner)" value={serverModal.name} onChange={(e) => setServerModal((p) => ({ ...p, name: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') saveServer(); }} />
          {!serverModal.id && <div className="form-text">Al añadirlo se generan sus dos tokens y se abre la ventana con los comandos para instalar el agente.</div>}
        </Modal>
      )}

      {connModal && (
        <Modal
          title={connModal.editing ? 'Editar conexión' : 'Conectar un proyecto a su servidor'}
          onClose={() => setConnModal(null)}
          footer={(
            <>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setConnModal(null)}>Cancelar</button>
              <button type="button" className="btn btn-primary" disabled={savingConn} onClick={saveConn}>{savingConn ? 'Guardando…' : 'Guardar'}</button>
            </>
          )}
        >
          {connError && <div className="alert alert-danger py-2">{connError}</div>}
          <div className="mb-3">
            <label className="form-label">Proyecto *</label>
            <Select
              options={projectOptions}
              value={projectOptions.find((o) => o.value === connModal.project_id) || null}
              onChange={(opt) => setConnModal((p) => ({ ...p, project_id: opt ? opt.value : null }))}
              isDisabled={connModal.editing}
              placeholder="Elige el proyecto…"
            />
            {connModal.project_id && <div className="form-text">En el servidor, su configuración va en <code>/opt/ajustes/proyectos/{connModal.project_id}.env</code></div>}
          </div>
          <div className="mb-3">
            <label className="form-label">Servidor *</label>
            <Select
              options={serverOptions}
              value={serverOptions.find((o) => o.value === connModal.server_id) || null}
              onChange={(opt) => setConnModal((p) => ({ ...p, server_id: opt ? opt.value : null }))}
              placeholder={servers.length ? 'Elige el servidor…' : 'Añade antes el servidor en la pestaña Servidores'}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Dirección de staging</label>
            <input className="form-control" placeholder="https://pruebas.cliente.com" value={connModal.staging_url} onChange={(e) => setConnModal((p) => ({ ...p, staging_url: e.target.value }))} />
            <div className="form-text">Déjala vacía solo si el servidor trabaja directamente sobre producción (<code>ENTORNO=produccion</code>). Con dirección de staging, nada se da por publicado sin pulsar Publicar.</div>
          </div>
          <div className="mb-3">
            <label className="form-label">Dirección de producción</label>
            <input className="form-control" placeholder="https://cliente.com" value={connModal.production_url} onChange={(e) => setConnModal((p) => ({ ...p, production_url: e.target.value }))} />
          </div>
          <div className="border rounded p-2 mb-3">
            <div className="fw-semibold small mb-2">Cómo trabaja Claude en este proyecto</div>
            <div className="row g-2">
              <div className="col-sm-6">
                <label className="form-label small mb-1">Modelo</label>
                <select className="form-select form-select-sm" value={connModal.claude_model} onChange={(e) => setConnModal((p) => ({ ...p, claude_model: e.target.value }))}>
                  {Object.entries(CLAUDE_MODELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="col-sm-6">
                <label className="form-label small mb-1">Esfuerzo</label>
                <select className="form-select form-select-sm" value={connModal.claude_effort} onChange={(e) => setConnModal((p) => ({ ...p, claude_effort: e.target.value }))}>
                  {Object.entries(CLAUDE_EFFORTS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="col-12">
                <label className="form-label small mb-1">Permisos</label>
                <select className="form-select form-select-sm" value={connModal.claude_permission_mode} onChange={(e) => setConnModal((p) => ({ ...p, claude_permission_mode: e.target.value }))}>
                  {Object.entries(CLAUDE_MODES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <div className="form-text">En los dos modos siguen vigentes los bloqueos fijos del servidor (leer <code>.env</code>, <code>sudo</code>, <code>git push</code>, <code>curl</code>…). Claude recibe además con cada cambio los últimos cambios hechos en el proyecto y puede apuntar notas en el <code>CLAUDE.md</code> del repositorio: esa es su memoria.</div>
              </div>
            </div>
          </div>
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id="ncs" checked={connModal.notify_client_staging} onChange={(e) => setConnModal((p) => ({ ...p, notify_client_staging: e.target.checked }))} />
            <label className="form-check-label" htmlFor="ncs">Avisar al cliente cuando esté listo para probar en staging</label>
          </div>
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id="ncp" checked={connModal.notify_client_published} onChange={(e) => setConnModal((p) => ({ ...p, notify_client_published: e.target.checked }))} />
            <label className="form-check-label" htmlFor="ncp">Avisar al cliente cuando esté publicado</label>
          </div>
          <div className="form-check mt-2">
            <input className="form-check-input" type="checkbox" id="nact" checked={connModal.active} onChange={(e) => setConnModal((p) => ({ ...p, active: e.target.checked }))} />
            <label className="form-check-label" htmlFor="nact">Conexión activa</label>
          </div>
        </Modal>
      )}

      {newModal && (
        <Modal
          title="Nuevo cambio que pide un cliente"
          onClose={() => setNewModal(false)}
          footer={(
            <>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setNewModal(false)}>Cancelar</button>
              <button type="button" className="btn btn-primary" disabled={savingNew} onClick={saveNew}>{savingNew ? 'Creando…' : 'Crear'}</button>
            </>
          )}
        >
          {formError && <div className="alert alert-danger py-2">{formError}</div>}
          <div className="mb-3">
            <label className="form-label">Cliente *</label>
            <Select
              options={clientOptions}
              value={clientOptions.find((o) => o.value === form.client_id) || null}
              onChange={(opt) => setForm((p) => ({ ...p, client_id: opt ? opt.value : null, project_id: null }))}
              placeholder="Elige el cliente…"
              isClearable
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Proyecto</label>
            <Select
              options={newProjectOptions}
              value={newProjectOptions.find((o) => o.value === form.project_id) || null}
              onChange={(opt) => setForm((p) => ({ ...p, project_id: opt ? opt.value : null }))}
              placeholder="Elige el proyecto…"
              isClearable
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Qué hay que cambiar *</label>
            <input className="form-control" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
          </div>
          <div className="mb-3">
            <label className="form-label">Detalle</label>
            <textarea className="form-control" rows="5" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} placeholder="Pantalla o sección, qué hay ahora, qué quiere el cliente, textos exactos…" />
          </div>
          <div>
            <label className="form-label">Prioridad</label>
            <select className="form-select" value={form.priority} onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}>
              {Object.entries(PRIORITY).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AjustesPage;
